
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Phone, 
  Mail, 
  Building2, 
  Euro,
  Calendar,
  Users,
  User
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CRMLead } from '@/hooks/useCRMLeads';
import { useAuth } from '@/hooks/useAuth';

interface LeadCardProps {
  lead: CRMLead;
  onDragStart: (e: React.DragEvent) => void;
  onClick: () => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelection?: () => void;
}

const LeadCard: React.FC<LeadCardProps> = ({ 
  lead, 
  onDragStart, 
  onClick, 
  selectionMode = false, 
  selected = false, 
  onToggleSelection 
}) => {
  const { user } = useAuth();
  const isMyLead = user && lead.owner_id === user.id;
  
  const getSourceColor = (source: string) => {
    switch (source) {
      case 'quickscan': return 'bg-blue-100 text-blue-800';
      case 'webform': return 'bg-green-100 text-green-800';
      case 'website': return 'bg-green-100 text-green-800';
      case 'hbe': return 'bg-amber-100 text-amber-800';
      case 'truck_calculator': return 'bg-emerald-100 text-emerald-800 font-semibold';
      case 'referral': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceBadgeText = (source: string, created_via?: string) => {
    if (created_via === 'contactformulier') return 'Contactformulier';
    if (source === 'hbe') return '🚗 HBE';
    if (source === 'truck_calculator') return '🚛 Diesel + HBE';
    if (source === 'website') return 'Website';
    return source;
  };

  const isOverdue = lead.next_action_date && new Date(lead.next_action_date) < new Date();
  const isContactForm = lead.created_via === 'contactformulier';
  
  const displayName = lead.organization?.name || 
                     lead.quickscan_data?.company_name || 
                     (lead.contact ? `${lead.contact.first_name || ''} ${lead.contact.last_name || ''}`.trim() : null) ||
                     'Onbekende lead';

  const displayEmail = lead.contact?.email || lead.quickscan_data?.email;

  return (
    <Card
      className={`cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200 border-l-4 ${
        !isContactForm && lead.score >= 70 
          ? 'border-l-green-500' 
          : !isContactForm && lead.score >= 40 
            ? 'border-l-yellow-500'
            : !isContactForm
              ? 'border-l-red-500'
              : 'border-l-green-500'
      } ${isOverdue ? 'ring-2 ring-red-200' : ''} ${
        selected ? 'ring-2 ring-primary bg-primary/5' : ''
      } shadow-sm`}
      draggable={!selectionMode}
      onDragStart={selectionMode ? undefined : onDragStart}
      onClick={onClick}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header with checkbox and title */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2 flex-1">
            {selectionMode && (
              <Checkbox 
                checked={selected} 
                onCheckedChange={onToggleSelection}
                onClick={(e) => e.stopPropagation()}
                className="mt-0.5"
              />
            )}
            <h4 className="font-semibold text-sm text-gray-900 truncate flex-1">
              {displayName}
            </h4>
          </div>
          <Badge className={`text-xs ml-2 flex-shrink-0 ${getSourceColor(lead.source)}`}>
            {getSourceBadgeText(lead.source, lead.created_via)}
          </Badge>
        </div>

        {/* Score badge only for non-contact form leads */}
        {!isContactForm && (
          <div>
            <Badge variant="outline" className="text-xs">
              Score: {lead.score}
            </Badge>
          </div>
        )}

        {/* Business Details for Quickscan */}
        {lead.quickscan_data && (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Building2 className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">{lead.quickscan_data.business_type}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">{lead.quickscan_data.employee_count} werknemers</span>
            </div>
            <div className="flex items-center gap-1">
              <Euro className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">
                €{lead.quickscan_data.monthly_energy_costs.toLocaleString()}/mnd
              </span>
            </div>
          </div>
        )}

        {/* Contact Actions */}
        <div className="flex items-center gap-2">
          {displayEmail && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`mailto:${displayEmail}`, '_blank');
              }}
            >
              <Mail className="h-3 w-3" />
            </Button>
          )}
          {lead.contact?.phone && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`tel:${lead.contact?.phone}`, '_blank');
              }}
            >
              <Phone className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Next Action */}
        {lead.next_action_date && (
          <div className={`text-xs p-2 rounded ${isOverdue ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>
                {format(new Date(lead.next_action_date), 'dd MMM', { locale: nl })}
              </span>
            </div>
          </div>
        )}

        {/* Assigned To */}
        <div className="flex items-center justify-between">
          {lead.owner && (
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <User className="h-3 w-3" />
              <span className="font-medium">{lead.owner.name || lead.owner.email}</span>
            </div>
          )}
          
          {/* Show assignment status with subtle badge */}
          {lead.owner_id && (
            <Badge 
              variant="outline" 
              className={`text-xs px-2 py-0.5 ${
                isMyLead 
                  ? 'bg-primary/10 text-primary border-primary/20' 
                  : 'bg-muted text-muted-foreground border-border'
              }`}
            >
              {isMyLead ? 'Mijn lead' : 'Toegewezen'}
            </Badge>
          )}
        </div>

        {/* Summary */}
        {lead.summary && (
          <div className="text-xs text-gray-400 border-t pt-2 truncate">
            {lead.summary}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadCard;
